import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router";
import { toast } from "sonner";
import { IconLock, IconLockOpen } from "@tabler/icons-react";
import { useBuild, useEvent, useNvpStatus, useWingNvpStatus, useCalibrations, useSignOffBuild, useUnlockBuild, useToeChecks, useYawChecks, useYawCheckPreviousEvent, useMetricsSnapshots } from "@/api/hooks";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { BuildHeader } from "@/components/build-header";
import { BuildNotesSection } from "@/components/build-notes-section";
import { BuildSectionCards, type BuildSection } from "@/components/build-section-cards";
import { CalibrationsSection } from "@/components/calibrations-section";
import { ChecksSection } from "@/components/checks-section";
import { MetricsSection } from "@/components/metrics-section";
import { WingCalHistorySection } from "@/components/wing-cal-history-section";
import { useWingCalHistory } from "@/api/hooks/use-wing-cals";
import { useCalibrationActions } from "@/hooks/use-calibration-actions";
import { useMetricsActions } from "@/hooks/use-metrics-actions";
import { useToeCheckActions } from "@/hooks/use-toe-check-actions";
import { useYawCheckActions } from "@/hooks/use-yaw-check-actions";
import { useAuth } from "@/providers/auth-provider";
import { asBuildId, asEventId } from "@/types/identifiers";

export function BuildDetailPage() {
  const { buildId: rawBuildId, eventId: rawEventId } = useParams();
  const buildId = rawBuildId ? asBuildId(rawBuildId) : undefined;
  const eventId = rawEventId ? asEventId(rawEventId) : undefined;
  const { data: build, isLoading: buildLoading } = useBuild(buildId!);
  const { data: event } = useEvent(eventId!);
  const { can, user } = useAuth();
  const { getNvpStatus, isBoatOnline, refresh: refreshNvp } = useNvpStatus();
  const { data: calibrations, isLoading: calsLoading } = useCalibrations(buildId!);
  const { data: toeChecks, isLoading: toeChecksLoading } = useToeChecks(buildId!);
  const { data: yawChecks, isLoading: yawChecksLoading } = useYawChecks(buildId!);
  const { data: previousEventYawCheck } = useYawCheckPreviousEvent(buildId!);
  const { data: wingHistory } = useWingCalHistory(buildId!);
  const { data: metricsSnapshots } = useMetricsSnapshots(buildId!);
  const { getWingNvpStatus } = useWingNvpStatus();

  const signOff = useSignOffBuild(buildId!);
  const unlock = useUnlockBuild(buildId!);

  useEffect(() => {
    refreshNvp();
  }, [refreshNvp]);

  const actions = useCalibrationActions(buildId!, calibrations);
  const metricsActions = useMetricsActions(buildId!);
  const toeCheckActions = useToeCheckActions(buildId!, toeChecks);
  const yawCheckActions = useYawCheckActions(buildId!, yawChecks);

  const [activeSection, setActiveSection] = useState<BuildSection>("calibrations");

  const isSignedOff = !!build?.signed_off_at;

  const handleSignOff = useCallback(() => {
    signOff.mutate(user.name, {
      onSuccess: () => toast.success("Build signed off"),
      onError: (err) => toast.error(err.message),
    });
  }, [signOff, user.name]);

  const handleUnlock = useCallback(() => {
    unlock.mutate(undefined, {
      onSuccess: () => toast.success("Build unlocked"),
      onError: (err) => toast.error(err.message),
    });
  }, [unlock]);

  if (!buildId || !eventId) return null;

  const calibrationCount = calibrations?.filter((c) => !c.hidden).length ?? 0;
  const goldenCount = calibrations?.filter((c) => c.state === "golden").length ?? 0;
  const wingCalCount =
    (wingHistory?.entries.length ?? 0) + (wingHistory?.current ? 1 : 0);

  return (
    <div>
      <PageHeader
        backLink={{ to: `/events/${eventId}`, label: "Fleet Overview" }}
        backLinkAction={
          can("view-builds") ? (
            <Link
              to={`/events/${eventId}/builds/${buildId}/audit`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Audit Log
            </Link>
          ) : undefined
        }
        action={
          <div className="flex items-center gap-4">
            {build && !isSignedOff && can("sign-off") && (
              <Button
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-500 hover:text-white"
                onClick={handleSignOff}
                disabled={signOff.isPending}
              >
                <IconLock className="mr-1.5 h-3.5 w-3.5" />
                {signOff.isPending ? "Signing off..." : "Sign off build"}
              </Button>
            )}
            {build && isSignedOff && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary/10 text-primary text-sm font-medium">
                  <IconLock className="h-4 w-4" />
                  <span>Signed off by {build.signed_off_by}</span>
                </div>
                {can("sign-off") && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnlock}
                    disabled={unlock.isPending}
                  >
                    <IconLockOpen className="mr-1.5 h-3.5 w-3.5" />
                    {unlock.isPending ? "Unlocking..." : "Unlock"}
                  </Button>
                )}
              </>
            )}
          </div>
        }
      >
        <div className="mt-3">
          <BuildHeader
            build={build}
            isLoading={buildLoading}
            nvpStatus={getNvpStatus(build?.boat_code)}
          />
        </div>
      </PageHeader>

      <BuildNotesSection
        buildId={buildId}
        notes={build?.notes}
        canEdit={can("manage-builds")}
        readOnly={isSignedOff}
      />

      <BuildSectionCards
        calibrationCount={calibrationCount}
        goldenCount={goldenCount}
        toeCheckCount={toeCheckActions.toeChecks.length}
        signedOffToeCheckCount={toeCheckActions.signedOffCount}
        yawCheckCount={yawCheckActions.yawChecks.length}
        signedOffYawCheckCount={yawCheckActions.signedOffCount}
        wingCalCount={wingCalCount}
        metricsCount={metricsSnapshots?.length ?? 0}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      />

      {activeSection === "calibrations" && (
        <CalibrationsSection
          buildId={buildId}
          calibrations={calibrations}
          isLoading={calsLoading}
          actions={actions}
          can={can}
          isBoatOnline={isBoatOnline(build?.boat_code)}
          readOnly={isSignedOff}
          eventName={event?.name}
          boatCode={build?.boat_code}
        />
      )}

      {activeSection === "wing-cals" && (
        <WingCalHistorySection
          buildId={buildId}
          can={can}
          wingNvpStatus={getWingNvpStatus(build?.boat_code)}
          readOnly={isSignedOff}
        />
      )}

      {/* Deliberately no readOnly gate: bots run at any time, incl. after build sign-off. */}
      {activeSection === "metrics" && (
        <MetricsSection
          buildId={buildId}
          actions={metricsActions}
          can={can}
          platformNvpStatus={getNvpStatus(build?.boat_code)}
          wingNvpStatus={getWingNvpStatus(build?.boat_code)}
        />
      )}

      {activeSection === "checks" && (
        <ChecksSection
          buildId={buildId}
          toeChecks={toeChecks}
          yawChecks={yawChecks}
          isLoading={toeChecksLoading || yawChecksLoading}
          toeActions={toeCheckActions}
          yawActions={yawCheckActions}
          can={can}
          readOnly={isSignedOff}
          previousEventYawCheck={previousEventYawCheck ?? undefined}
        />
      )}
    </div>
  );
}
